import React from 'react';
import {
    Paper,
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Chip,
    useTheme,
} from '@mui/material';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from 'recharts';
import { Room } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    MEASURE_CONFIG,
    MEASURE_TYPES,
    getMeasureIcon,
    getMeasureLabel,
    getMeasureUnit,
} from '../../utils/measurementConfig';
import { getMonthName } from '../../utils/monthlyDataUtils';

function CustomBarTooltip({ active, payload, label, t, selectedMonth, selectedYear }) {
    const theme = useTheme();

    if (!active || !payload || payload.length === 0) return null;

    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                bgcolor: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1.5,
                boxShadow: 4,
                minWidth: 160,
                backdropFilter: 'blur(4px)',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Room fontSize="small" color="primary" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {label}
                </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {getMonthName(selectedMonth, t)} {selectedYear}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {payload.map((entry, idx) => {
                    const measureType = entry.dataKey;
                    const config = MEASURE_CONFIG[measureType] || {};
                    return (
                        <Box
                            key={`bar-tooltip-${idx}`}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 2,
                                fontSize: '0.8125rem',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        bgcolor: config.color || entry.fill,
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {getMeasureLabel(measureType, t)}
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {entry.value} {getMeasureUnit(measureType)}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function MonthlyZoneComparison({
    comparisonData,
    normalizedData = [],
    selectedMonth,
    selectedYear,
    activeMeasure = 'ALL',
    viewMode = 'bar', // 'bar' | 'cards' | 'both'
}) {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const { chartData, seriesKeys } = comparisonData || { chartData: [], seriesKeys: [] };
    const monthName = getMonthName(selectedMonth, t);

    // Group normalized data by zone for cards
    const zoneCardsData = chartData;

    const showBar = viewMode === 'bar' || viewMode === 'both';
    const showCards = viewMode === 'cards' || viewMode === 'both';

    return (
        <Box sx={{ mb: 3 }}>
            {/* Comparative Bar Chart */}
            {showBar && (
                <Paper
                    elevation={2}
                    sx={{
                        p: { xs: 2, sm: 3 },
                        mb: 3,
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                    }}
                >
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {t('common.zoneComparison', 'Zone Comparison')} - {monthName} {selectedYear}
                        </Typography>
                        <Chip
                            label={`${chartData.length} ${t('common.activeZones', 'Zones')}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    </Box>

                    <Box sx={{ width: '100%', height: 380 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={chartData}
                                margin={{ top: 10, right: 30, left: 10, bottom: 25 }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}
                                />
                                <XAxis
                                    dataKey="zone"
                                    stroke={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'}
                                    tick={{ fontSize: 12 }}
                                    interval={0}
                                    angle={-15}
                                    textAnchor="end"
                                />
                                <YAxis
                                    stroke={isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'}
                                    tick={{ fontSize: 11 }}
                                />
                                <Tooltip
                                    content={
                                        <CustomBarTooltip
                                            t={t}
                                            selectedMonth={selectedMonth}
                                            selectedYear={selectedYear}
                                        />
                                    }
                                />
                                <Legend
                                    wrapperStyle={{ paddingTop: 15 }}
                                    formatter={(value) => (
                                        <span style={{ color: isDark ? '#fff' : '#333', fontSize: 13, fontWeight: 500 }}>
                                            {getMeasureLabel(value, t)} ({getMeasureUnit(value)})
                                        </span>
                                    )}
                                />

                                {seriesKeys.map((key) => {
                                    const config = MEASURE_CONFIG[key] || {};
                                    return (
                                        <Bar
                                            key={key}
                                            dataKey={key}
                                            name={getMeasureLabel(key, t)}
                                            fill={config.color}
                                            radius={[4, 4, 0, 0]}
                                            maxBarSize={50}
                                        />
                                    );
                                })}
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </Paper>
            )}

            {/* Zone Summary Cards Grid */}
            {showCards && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        {t('common.zoneSummaryCards', 'Zone Summaries')} - {monthName} {selectedYear}
                    </Typography>

                    <Grid container spacing={2.5}>
                        {zoneCardsData.map((zoneItem) => {
                            return (
                                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={zoneItem.zone}>
                                    <Card
                                        elevation={2}
                                        sx={{
                                            height: '100%',
                                            borderRadius: 2,
                                            transition: 'transform 0.2s, box-shadow 0.2s',
                                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 5 },
                                        }}
                                    >
                                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                <Room color="primary" fontSize="small" />
                                                <Typography variant="h6" component="div" sx={{ fontWeight: 700 }} noWrap>
                                                    {zoneItem.zone}
                                                </Typography>
                                            </Box>

                                            <Grid container spacing={1.5}>
                                                {MEASURE_TYPES.map((type) => {
                                                    const config = MEASURE_CONFIG[type];
                                                    const val = zoneItem[type];
                                                    const isSelected = activeMeasure === type || activeMeasure === 'ALL';

                                                    return (
                                                        <Grid size={{ xs: 6 }} key={type}>
                                                            <Box
                                                                sx={{
                                                                    p: 1.25,
                                                                    borderRadius: 1.5,
                                                                    bgcolor: isSelected ? config.lightBg : 'action.hover',
                                                                    border: '1px solid',
                                                                    borderColor: isSelected ? `${config.color}33` : 'transparent',
                                                                }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                                    {getMeasureIcon(type, { fontSize: 16 })}
                                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                                        {getMeasureLabel(type, t)}
                                                                    </Typography>
                                                                </Box>
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        fontWeight: 700,
                                                                        color: val !== undefined && val !== null ? config.color : 'text.disabled',
                                                                    }}
                                                                >
                                                                    {val !== undefined && val !== null
                                                                        ? `${val} ${config.unit}`
                                                                        : '—'}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            )}
        </Box>
    );
}

export default MonthlyZoneComparison;
